// RekhaLoop.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faLightbulb, faCheckCircle, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function RekhaLoop({ gridSize = 5, youtubeLink }) {
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);
  const [completed, setCompleted] = useState(false);

  // Initialize edges: horizontal and vertical
  const initEdges = () => {
    const horizontal = Array(gridSize + 1)
      .fill(null)
      .map(() => Array(gridSize).fill(false));
    const vertical = Array(gridSize)
      .fill(null)
      .map(() => Array(gridSize + 1).fill(false));
    return { horizontal, vertical };
  };

  const [edges, setEdges] = useState(initEdges());
  const [history, setHistory] = useState([]);

  const toggleEdge = (type, r, c) => {
    const newEdges = { ...edges, [type]: edges[type].map(row => [...row]) };
    newEdges[type][r][c] = !newEdges[type][r][c];
    setHistory([...history, edges]);
    setEdges(newEdges);
  };

  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setEdges(last);
  };

  const giveHint = () => {
    // For demo, just toggle a random horizontal edge
    const r = Math.floor(Math.random() * edges.horizontal.length);
    const c = Math.floor(Math.random() * edges.horizontal[0].length);
    toggleEdge('horizontal', r, c);
  };

  const checkSolution = () => {
    // Dummy check for demo
    const isCorrect = true; // Replace with actual logic
    setCompleted(isCorrect);
    if (!isCorrect) {
      setNotification({ type: 'error', message: '❌ Not correct yet. Keep trying!' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <div className="p-2 min-h-screen bg-black text-gray-300 flex flex-col items-center">

      {/* Top Buttons */}
      <div className="w-full flex justify-center gap-4 items-center mb-4">
        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex justify-center items-center gap-2"
          onClick={() => window.history.back()}
        >
          ← Back
        </button>

        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-blue-600 text-black rounded-lg hover:bg-blue-500 flex justify-center items-center gap-2"
          onClick={() => setShowHelp(true)}
        >
          <FontAwesomeIcon icon={faQuestionCircle} /> Help
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 p-6 rounded-xl max-w-lg w-full text-gray-300 flex flex-col gap-4">
            <h3 className="text-m lg:text-xl font-bold text-green-400">📖 How to Play Rekha-Loop</h3>
            <p className='text-m lg:text-lg'>
              Draw lines around the grid so that a single continuous loop is formed. Each number in a cell (if present) indicates how many edges of that cell must be part of the loop.
            </p>
            <ul className="list-disc list-inside text-gray-400">
              <li>Click on the horizontal or vertical edge to toggle the line.</li>
              <li>Use hints to fill one line correctly.</li>
              <li>Undo previous moves with the Undo button.</li>
              <li>Press Check when you think the puzzle is complete.</li>
            </ul>

            {youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-red-600 text-black rounded-lg hover:bg-red-500 text-center font-semibold"
              >
                ▶ Watch
              </a>
            )}

            <button
              className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 mt-2"
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {completed && (
        <div className="text-green-400 text-xl flex items-center gap-2 mb-2 animate-pulse">
          <FontAwesomeIcon icon={faCheckCircle} /> Puzzle Completed!
        </div>
      )}

      <h2 className="text-lg lg:text-2xl font-bold mb-2 text-green-400">🔗 Rekha-Loop (Slitherlink)</h2>
      <p className="text-gray-400 mb-4 text-center max-w-md text-xs lg:text-m">
        Click edges to form a continuous loop. Use hints and undo to complete the puzzle!
      </p>

      {/* Grid */}
      <div className="flex flex-col items-center">
        {Array(gridSize).fill(null).map((_, rIdx) => (
          <div key={rIdx} className="flex flex-col">
            {/* Horizontal edges */}
            <div className="flex">
              {Array(gridSize).fill(null).map((_, cIdx) => (
                <div key={cIdx} className="flex items-center">
                  <div
                    onClick={() => toggleEdge('horizontal', rIdx, cIdx)}
                    className={`w-12 h-2 m-0.5 cursor-pointer ${edges.horizontal[rIdx][cIdx] ? 'bg-green-400' : 'bg-gray-800 hover:bg-gray-700'}`}
                  ></div>
                  <div className="w-6 h-6"></div>
                </div>
              ))}
            </div>

            {/* Vertical edges */}
            <div className="flex">
              {Array(gridSize + 1).fill(null).map((_, cIdx) => (
                <div
                  key={cIdx}
                  onClick={() => toggleEdge('vertical', rIdx, cIdx)}
                  className={`w-2 h-12 m-0.5 cursor-pointer ${edges.vertical[rIdx][cIdx] ? 'bg-green-400' : 'bg-gray-800 hover:bg-gray-700'}`}
                ></div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="p-4 flex flex-row gap-4 flex-wrap justify-center">
        <button
          onClick={checkSolution}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-green-600 text-black font-semibold rounded-lg hover:bg-green-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faCheckCircle} /> Check
        </button>
        <button
          onClick={undo}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faUndo} /> Undo
        </button>
        <button
          onClick={giveHint}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-blue-600 text-black font-semibold hover:bg-blue-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faLightbulb} /> Hint
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-sm font-semibold ${
            notification.type === 'error' ? 'bg-red-700 text-white' : 'bg-green-600 text-black'
          } flex justify-between items-center shadow-lg z-50 w-full max-w-md`}
        >
          <span>{notification.message}</span>
          <button
            className="w-10 p-1 text-lg flex items-center justify-center bg-red-800"
            onClick={() => setNotification(null)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

    </div>
  );
}
